import React, { useEffect } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { Outlet, useParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ManagePassesWrapper } from './ManagePassesWrapper';
import { ManagePassesProvider, useManagePasses } from './ManagePassesContext';
import RotatingLoader from '../ui/RotatingLoader';
import { AccountStatus } from '../api';
import { AccountNotActive } from '../shared/ui/AccountNotActive';
import { registerPassesSW } from './pwa/registerPassesSW';
import { registerNativeDeepLink } from './pwa/registerNativeDeepLink';
import { OfflineBanner } from './pwa/OfflineBanner';

const ManagePassesLayout = () => {
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const { account, isLoading, isLoggedIn, email } = useManagePasses();
  const posthog = usePostHog();

  useEffect(() => {
    if (!account) return;
    posthog.setPersonProperties({
      accountId: account.id,
      accountName: account.name,
      context: 'passes',
      environment: import.meta.env.VITE_REACT_APP_ENVIRONMENT || 'local',
    });
  }, [account, posthog]);

  useEffect(() => {
    if (!email) return;
    posthog.identify(email);
    posthog.setPersonProperties({ email });
  }, [email, posthog]);

  if (account && account.status === AccountStatus.PAUSED) {
    return <AccountNotActive />;
  }

  return (
    <ManagePassesWrapper account={account}>
      {isLoading && isLoggedIn ? (
        <Grid container height="100dvh" justifyContent="center" alignItems="center">
          <RotatingLoader />
        </Grid>
      ) : (
        <Grid
          container
          flexDirection="column"
          px={isSmallScreen ? 2 : 3}
          py={isSmallScreen ? 1.5 : 3}
          gap={isSmallScreen ? 1.5 : 3}>
          <Outlet />
        </Grid>
      )}
    </ManagePassesWrapper>
  );
};

export const ManagePassesPage = () => {
  const { accountId } = useParams<{ accountId?: string }>();

  useEffect(() => {
    registerPassesSW();
    registerNativeDeepLink();
  }, []);

  return (
    <HelmetProvider>
      <Helmet>
        <link rel="manifest" href="/passes/manifest.webmanifest" />
        <meta name="theme-color" content="#71B266" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="Passes" />
      </Helmet>
      <ManagePassesProvider preloadedAccountId={accountId}>
        <ManagePassesLayout />
        <OfflineBanner />
      </ManagePassesProvider>
    </HelmetProvider>
  );
};
